import {
    BooleanField,
    Datagrid,
    DateField,
    EditButton,
    ListButton,
    ReferenceManyField,
    Show,
    SimpleShowLayout,
    Tab,
    TabbedShowLayout,
    TextField,
    TopToolbar,
    usePermissions,
    useRecordContext,
} from 'react-admin';
import { Button, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import type { Client, ClientStatus, Build, BuildStatus } from '../../types';
import { BuildReadinessPanel, TemplateChip } from './TemplateFields';

const StatusField = () => {
    const record = useRecordContext<Client>();
    if (!record) return null;

    const statusColors: Record<ClientStatus, 'default' | 'success' | 'warning'> = {
        pending: 'warning',
        active: 'success',
        inactive: 'default',
    };

    return (
        <Chip
            label={record.status}
            color={statusColors[record.status]}
            size="small"
        />
    );
};

const BuildStatusField = () => {
    const record = useRecordContext<Build>();
    if (!record) return null;

    const statusColors: Record<BuildStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
        queued: 'default',
        preparing: 'info',
        in_progress: 'warning',
        building: 'warning',
        uploading: 'info',
        publishing: 'info',
        completed: 'success',
        failed: 'error',
        timeout: 'error',
    };

    return (
        <Chip
            label={record.status}
            color={statusColors[record.status]}
            size="small"
        />
    );
};

const ShowActions = () => {
    const record = useRecordContext<Client>();
    const { permissions } = usePermissions();

    return (
        <TopToolbar>
            <ListButton/>
            {permissions?.includes('clients:write') && <EditButton/>}
            {record && permissions?.includes('configurations:read') && (
                <Button
                    component={Link}
                    to={`/clients/${record.id}/configuration`}
                    variant="contained"
                    size="small"
                >
                    Configuration
                </Button>
            )}
            {record && permissions?.includes('secrets:write') && (
                <Button
                    component={Link}
                    to={`/clients/${record.id}/secrets`}
                    variant="contained"
                    size="small"
                >
                    Secrets
                </Button>
            )}
        </TopToolbar>
    );
};

export const ClientShow = () => {
    const { permissions } = usePermissions();

    return (
        <Show actions={<ShowActions/>}>
            <TabbedShowLayout>
                <Tab label="Summary">
                    <SimpleShowLayout>
                        <TextField source="id"/>
                        <TextField source="name"/>
                        <TextField source="slug"/>
                        <StatusField/>
                        <TemplateChip label="Template · version"/>
                        <BuildReadinessPanel label="Build readiness"/>
                        <DateField source="lastBuildAt" showTime/>
                        <DateField source="createdAt" showTime/>
                        <DateField source="updatedAt" showTime/>
                        <TextField source="createdBy"/>
                        <BooleanField source="useSharedIosAccount" />
                        <BooleanField source="useSharedAndroidAccount" />
                    </SimpleShowLayout>
                </Tab>

                {permissions?.includes('builds:read') && (
                    <Tab label="Builds">
                        <ReferenceManyField
                            reference="builds"
                            target="clientId"
                            label={false}
                        >
                            <Datagrid rowClick="show" bulkActionButtons={false}>
                                <TextField source="templateKey" label="Template"/>
                                <TextField source="coreVersion" label="Template Version"/>
                                <BuildStatusField />
                                <TextField source="triggerType"/>
                                <DateField source="createdAt" showTime/>
                                <DateField source="completedAt" showTime/>
                            </Datagrid>
                        </ReferenceManyField>
                    </Tab>
                )}
            </TabbedShowLayout>
        </Show>
    );
};
